import { useState, useEffect, createContext, useRef } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import {
    AccountInfo,
    AuthenticationResult,
    InteractionRequiredAuthError,
    InteractionStatus,
} from '@azure/msal-browser';
import SETTINGS from '../settings.json';
import { BasicIDToken } from '../interfaces/ApplicationInterfaces';

export const AccessTokenContext = createContext<string | undefined>(undefined);
const STORAGE_UI_LOCALES_KEY = 'usermgmt.ui_locales';

export default function useAccessToken() {
    const isAuthenticated = useIsAuthenticated();
    const { accounts, inProgress, instance } = useMsal();
    const tokenRefreshTimeout = useRef<number | undefined>(undefined);
    const tokenRefreshCallback = useRef<undefined | (() => Promise<string | undefined>)>(undefined);
    const [uiLocale, setUiLocale] = useState<string>(new URLSearchParams(window.location.search).get('ui_locales')?.substring(0,16) ?? (window.sessionStorage.getItem(STORAGE_UI_LOCALES_KEY) ?? ''));

    let searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has('ui_locales')) {
        // 16 characters is enough for the longest locale codes with some to spare
        // Prevents adding arbitrarily-large strings to session storage
        const newUiLocale = searchParams.get('ui_locales')?.substring(0, 16) ?? '';
        window.sessionStorage.setItem(STORAGE_UI_LOCALES_KEY, newUiLocale);
        if (uiLocale != newUiLocale) {
            setUiLocale(newUiLocale);
        }
    }

    const findAccount = (): AccountInfo | undefined => {
        return accounts.find(
            (account) =>
                account.idTokenClaims !== undefined &&
                SETTINGS.authorityDomain.includes(account.environment) &&
                (account.idTokenClaims as BasicIDToken).aud === SETTINGS.msalClientId &&
                (account.idTokenClaims as any).exp > Math.floor(Date.now() / 1000)
        );
    };

    const fetchToken = async (): Promise<AuthenticationResult | undefined> => {
        if (isAuthenticated && inProgress === InteractionStatus.None) {
            let currentAccount = findAccount();
            if(currentAccount === null || currentAccount === undefined){
                instance.loginRedirect({
                    scopes: SETTINGS.msalScope !== '' ? [SETTINGS.msalScope] : [],
                    redirectUri: window.location.origin,
                    account: undefined,
                    extraQueryParameters: {
                        ...((!!uiLocale) && {ui_locales: uiLocale})
                    }
                });
            }

            const accessTokenRequest = {
                scopes: SETTINGS.msalScope !== '' ? [SETTINGS.msalScope] : [],
                redirectUri: window.location.origin,
                account: currentAccount,
                extraQueryParameters: {
                    ...((!!uiLocale) && {ui_locales: uiLocale})
                }
            };

            let tokenAcquisitionError = null;
            const tokenResponse = await instance
                .acquireTokenSilent(accessTokenRequest)
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        console.log(`Interaction required: ${error}`);
                        tokenAcquisitionError = error;
                    }
                    else {
                        tokenAcquisitionError = error;
                    }
                    console.log(error);
                });
            if (
                tokenAcquisitionError !== undefined &&
                tokenAcquisitionError !== null
            ) {
                await instance.loginRedirect(accessTokenRequest);
            }
            if (tokenResponse) {
                return tokenResponse;
            }
        }
    }

    const getTokClaims = () => {
        return fetchToken().then(
            (tokenResponse) => {
                if (tokenResponse) {
                    return tokenResponse.idTokenClaims;
                }
            }
        )
    }

    const accessTokenRequest = () => {
        return fetchToken().then(
            (tokenResponse) => {
                if(tokenResponse) {
                    // schedule token refresh
                    //@ts-ignore
                    const expiration = tokenResponse.idTokenClaims.exp * 1000;
                    console.log(`Token expires at ${new Date(expiration).toTimeString()}`)
                    //expiration is in milliseconds since 1970
                    const now = Date.now();
                    // refresh 1 minute before expiration
                    const delay = expiration - now - (1 * 60 * 1000);
                    if (tokenRefreshTimeout.current !== undefined) {
                        clearTimeout(tokenRefreshTimeout.current);
                        tokenRefreshTimeout.current = undefined;
                    }
                    tokenRefreshCallback.current = accessTokenRequest;
                    tokenRefreshTimeout.current = setTimeout(() => {
                        tokenRefreshTimeout.current = undefined;
                        if (tokenRefreshCallback.current) {
                            tokenRefreshCallback.current();
                        }
                    }, delay);
                    return tokenResponse.idToken;
                } else {
                    console.log('No token response', tokenResponse)
                }
            } 
        );
    }

    useEffect(() => {
        console.log('useEffect', inProgress);
        accessTokenRequest();
    }, [inProgress]);

    return { getToken: async () => { return await accessTokenRequest(); }, uiLocale, getTokenClaims: async () => await getTokClaims() };
}